import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../lib/auth-context';
import { Document } from '../types/Document';
import { API_URL } from '@/lib/config';

export interface UseDocumentDataReturn {
  document: Document | null;
  loading: boolean;
  error: string;
  fetchDocument: () => Promise<void>;
  editingTitle: boolean;
  newTitle: string;
  setNewTitle: React.Dispatch<React.SetStateAction<string>>;
  savingTitle: boolean;
  handleSaveTitle: () => Promise<void>;
  cancelTitleEdit: () => void;
  setEditingTitle: React.Dispatch<React.SetStateAction<boolean>>;
}

export const useDocumentData = (onResultsNeeded: (documentId: string) => void): UseDocumentDataReturn => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [document, setDocument] = useState<Document | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingTitle, setEditingTitle] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [savingTitle, setSavingTitle] = useState(false);

  const fetchDocument = useCallback(async () => {
    if (!id || !user?.token) return;

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/documents/${id}`, {
        headers: {
          'Authorization': `Bearer ${user.token}`,
        },
      });

      if (!response.ok) {
        let errorMsg = 'Failed to fetch document';
        try {
            const errorData = await response.json();
            errorMsg = errorData.detail || errorData.message || errorMsg;
        } catch (e) { /* Ignore if response is not json */ }
        throw new Error(errorMsg);
      }

      const data = await response.json();
      setDocument(data);
      setNewTitle(data.originalName); // Set initial title for editing

      if (data.processingStatus === 'done') {
        onResultsNeeded(id);
      }
    } catch (err) {
      console.error('Error fetching document:', err);
      setError(err instanceof Error ? err.message : 'Error fetching document');
    } finally {
      setLoading(false);
    }
  }, [id, user?.token, onResultsNeeded]);

  useEffect(() => {
    fetchDocument();
  }, [fetchDocument]);

  const handleSaveTitle = async () => {
    if (!id || !user?.token || !newTitle.trim()) return;

    setSavingTitle(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/documents/${id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${user.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ originalName: newTitle.trim() }),
      });

      if (!response.ok) {
        let errorMsg = 'Failed to update document title';
        try {
            const errorData = await response.json();
            errorMsg = errorData.detail || errorData.message || errorMsg;
        } catch (e) { /* Ignore if response is not json */ }
        throw new Error(errorMsg);
      }

      if (document) {
        setDocument({
          ...document,
          originalName: newTitle.trim(),
        });
      }
      setEditingTitle(false);
    } catch (err) {
      console.error('Error updating document title:', err);
      setError(err instanceof Error ? err.message : 'Error updating document title');
    } finally {
      setSavingTitle(false);
    }
  };

  const cancelTitleEdit = () => {
    setEditingTitle(false);
    if (document) {
      setNewTitle(document.originalName);
    }
  };

  return {
    document,
    loading,
    error,
    fetchDocument,
    editingTitle,
    newTitle,
    setNewTitle,
    savingTitle,
    handleSaveTitle,
    cancelTitleEdit,
    setEditingTitle,
  };
}; 